<template>
  <v-dialog
    v-model="aIDetailDialog"
    :max-width="modalLayout.MD"
    v-bind="modalP2FormViewport"
    @keydown.esc="handleClose"
    @close="handleClose"
  >
    <v-card>
      <CrudFormDialogHeader
        :kicker="$t('aiJobTable.title')"
        :title="$t('aiDetail.dialogTitle')"
        show-close
        @close="handleClose"
      />
      <v-card-text>
        <!-- 분석 대상 정보 섹션 -->
        <div class="info-section">
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ $t('aiDetail.targetInfo') }}</div>
          <v-row
            v-if="aiJob?.siteName"
            class="detail-row"
          >
            <v-col
              cols="3"
              class="ai-detail-field-label"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('siteName') }}
            </v-col>
            <v-col
              cols="9"
              class="ai-detail-field-value--emphasis"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('siteName', aiJob.siteName) }}
            </v-col>
          </v-row>
          <v-row
            v-if="aiJob?.reportName"
            class="detail-row"
          >
            <v-col
              cols="3"
              class="ai-detail-field-label"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('reportName') }}
            </v-col>
            <v-col
              cols="9"
              class="ai-detail-field-value--emphasis"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('reportName', aiJob.reportName) }}
            </v-col>
          </v-row>
          <v-row
            v-if="aiJob?.examineDate"
            class="detail-row"
          >
            <v-col
              cols="3"
              class="ai-detail-field-label"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('examineDate') }}
            </v-col>
            <v-col
              cols="9"
              class="ai-detail-field-value--emphasis"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('examineDate', aiJob.examineDate) }}
            </v-col>
          </v-row>
          <v-row
            v-if="aiJob?.jobRefSeq != null"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('jobRefSeq') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('jobRefSeq', aiJob.jobRefSeq) }}
            </v-col>
          </v-row>
          <v-row
            v-if="aiJob?.jobStartTime"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('jobStartTime') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('jobStartTime', aiJob.jobStartTime) }}
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4" />

        <!-- 진행 상태 정보 섹션 -->
        <div class="info-section">
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ $t('aiDetail.progressInfo') }}</div>
          <v-row
            v-if="aiJob?.processStatus"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('processStatus') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
                {{ valueContent('processStatus', aiJob.processStatus) }}
            </v-col>
          </v-row>
          <v-row
            v-if="aiJob?.jobKinds || aiJob?.samplingFlag !== undefined"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ $t('aiDetail.jobKinds') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              <div v-if="aiJob?.jobKinds">
                {{ formatJobKinds(aiJob.jobKinds) }}
              </div>
              <div
                v-if="executionModeCaption"
                class="text-caption text--secondary"
                :class="{ 'mt-1': aiJob?.jobKinds }"
              >
                {{ executionModeCaption }}
              </div>
            </v-col>
          </v-row>
          <v-row class="detail-row">
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ $t('aiDetail.progressRate') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ formatProcessRate(aiJob) }}
            </v-col>
          </v-row>
          <v-row
            v-if="aiJob?.processStatus === 'FLR' && aiJob?.jobFailReason"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ $t('aiDetail.failReasonShort') }}
            </v-col>
            <v-col
              cols="9"
              class="detail-value-fail"
            >
              {{ aiJob.jobFailReason }}
            </v-col>
          </v-row>
        </div>

        <v-divider v-if="hasRgbLocJob() || hasIrLocJob() || hasIrDataJob() || hasIrDefectJob()" class="my-4" />

        <!-- 이미지 통계 섹션 -->
        <div v-if="hasRgbLocJob() || hasIrLocJob()" class="info-section">
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ $t('aiDetail.imageStats') }}</div>
          <v-row
            v-if="hasIrLocJob() && aiJob?.thermalImageCount !== undefined"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('thermalImageCount') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('thermalImageCount', aiJob.thermalImageCount) }}
            </v-col>
          </v-row>
          <v-row
            v-if="hasRgbLocJob() && aiJob?.rgbImageCount !== undefined"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('rgbImageCount') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('rgbImageCount', aiJob.rgbImageCount) }}
            </v-col>
          </v-row>
        </div>

        <v-divider v-if="hasIrDataJob() && hasIrDefectJob()" class="my-4" />

        <!-- 결함 통계 섹션 -->
        <div v-if="hasIrDefectJob()" class="info-section">
          <div class="text-subtitle-1 font-weight-medium mb-3">{{ $t('aiDetail.defectStats') }}</div>
          <v-row
            v-if="hasIrDefectJob() && aiJob?.detectDefectCount !== undefined"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('detectDefectCount') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('detectDefectCount', aiJob.detectDefectCount) }}
            </v-col>
          </v-row>
          <v-row
            v-if="hasIrDefectJob() && aiJob?.defectConfirmCount !== undefined"
            class="detail-row"
          >
            <v-col
              cols="3"
              style="padding-right: 0; padding-bottom: 0"
            >
              {{ titleContent('defectConfirmCount') }}
            </v-col>
            <v-col
              cols="9"
              style="padding-left: 0; padding-bottom: 0"
            >
              {{ valueContent('defectConfirmCount', aiJob.defectConfirmCount) }}
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import baseApi from '@/apis/base.api';
import { normalizeJobKinds, formatJobKinds } from '@/utils/aiJobUtils';
import { AI_JOB_KINDS } from '@/constants/aiJobKinds';
import { getStatusText, getStatusColor } from '@/constants/progressStatus';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'AIDetailDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    //dialog, callbackFun(주소위치(altitude,latitude), addressName(도로명 주소)), )
    aIDetailDialog: {
      type: Boolean,
      required: true
    },
    currentJobId: {
      type: Number,
      required: true
    }
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
    aiJob: null,
    page: 1,
    totalItems: 0,
    keyword: '',
    itemsPerPage: 5,
    pageCount: 0,
    paging: true
  }),
  watch: {
    currentJobId: {
      deep: true,
      handler(newVal) {
        this.getAIData();
      }
    }
  },
  mounted() {
    if (this.$props.currentJobId == 0) return;
    this.getAIData();
  },
  computed: {
    executionModeCaption() {
      if (!this.aiJob || this.aiJob.samplingFlag === undefined) {
        return '';
      }
      return this.aiJob.samplingFlag
        ? this.$t('aiDetail.ranAsSampling')
        : this.$t('aiDetail.ranAsFull');
    }
  },
  methods: {
    handleClose() {
      //모달 초기화
      this.aIDetailDialog = false;
      this.$emit('close', {});
    },
    titleContent(name) {
      const key = `aiDetail.fields.${name}`;
      return this.$te(key) ? this.$t(key) : name;
    },
    valueContent(name, value) {
      /**
       * "thermalImageCount": 100, "rgbImageCount": 112, "processStatus": "FLR", "siteName": "테스트사이트", "reportName": "테스트검사2410241", "jobStartTime": "2024-11-20T02:27:19.603+00:00", "jobEndTime": null, "detectDefectCount": 0, "defectConfirmCount": 0 }
       *
       */
      switch (name) {
      case 'processStatus':
        return getStatusText(value);
      case 'examineDate':
        return value || '';
      case 'jobStartTime':
        return value ? new Date(value).toLocaleString() : '';
      case 'jobEndTime':
        return value ? new Date(value).toLocaleString() : '';
      default:
        return value;
      }
    },
    getPageCount(totalCount, countPerPage) {
      if (totalCount === 0 || countPerPage == 0) return 1;
      const hasRest = totalCount % countPerPage != 0;
      return Math.floor(totalCount / countPerPage + (hasRest ? 1 : 0));
    },
    async getAIData() {
      console.log('getAIData', this.$props.currentJobId);
      try {
        const result = await baseApi.get(`ai/${this.$props.currentJobId}`);
        this.aiJob = result?.data?.content;
        this.error = false;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
   
    formatProcessRate(aiJob) {
      if (!aiJob) return '-';
      if (aiJob.processRate !== null && aiJob.processRate !== undefined) {
        return Math.round(aiJob.processRate * 100) + '%';
      }
      return '-';
    },
    formatJobKinds,
    hasJobKind(jobKind) {
      if (!this.aiJob || !this.aiJob.jobKinds) {
        return false;
      }
      const normalized = normalizeJobKinds(this.aiJob.jobKinds);
      return normalized.includes(jobKind);
    },
    hasRgbLocJob() {
      return this.hasJobKind(AI_JOB_KINDS.RGB_LOC);
    },
    hasIrLocJob() {
      return this.hasJobKind(AI_JOB_KINDS.IR_LOC);
    },
    hasIrDataJob() {
      return this.hasJobKind(AI_JOB_KINDS.IR_DATA);
    },
    hasIrDefectJob() {
      return this.hasJobKind(AI_JOB_KINDS.IR_DEFECT);
    },
    getStatusColor,
    getStatusIcon(status) {
      switch (status) {
      case 'BEF':
        return 'mdi-clock-outline';
      case 'ING':
        return 'mdi-progress-clock';
      case 'FIN':
        return 'mdi-check-circle';
      case 'FLR':
        return 'mdi-alert-circle';
      case 'UNK':
        return 'mdi-help-circle';
      default:
        return 'mdi-help-circle';
      }
    }
  }
};
</script>
<style scoped>
.address-item:hover {
  background-color: rgba(127, 127, 127, 0.12);
}
.address-item {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid rgba(127, 127, 127, 0.2);
  cursor: pointer;
}

.info-section > .text-subtitle-1 {
  color: var(--v-text-base);
}

.detail-row {
  margin: 0;
  color: var(--v-text-base);
}

/* 라벨: 다크에서 검은 폴백(--v-text-secondary 없음) 금지 → secondary 토큰 */
.detail-row > .col:first-child,
.ai-detail-field-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--v-secondary-base) !important;
}

.detail-row > .col:last-child:not(.detail-value-fail),
.ai-detail-field-value--emphasis {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--v-text-base);
}

.ai-detail-field-value--emphasis {
  font-weight: 600;
}

.detail-value-fail {
  padding-left: 0;
  padding-bottom: 0;
  color: var(--v-error-base) !important;
}
</style>
